// doc/11 PR-A4a: Knowledge Graph view MVP. Renders a project's cards +
// typed edges as a reactflow graph laid out hierarchically by dagre.
//
// Visual encoding per doc/11 §4 PR-A4a:
//   - Node color/style by kind (decision = bold dark; dead_end = struck;
//     pivot = amber; gap = amber highlight; observation = dashed; ...)
//   - Edge style by type (depends-on = thick solid; supports = thin solid;
//     contradicts = red dashed; derived-from = gray thin; others = neutral)
//   - Stale nodes get an amber overlay.
// Interaction:
//   - Click node → open CardPeek (via onPeek prop)
//   - Hover node → highlight 1-hop neighbors + thicken their edges
//
// MVP scope per doc/11: read-only, dagre-only, no focus mode / filters /
// export. PR-A4b adds those interactions.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  Handle,
  type Edge as RFEdge,
  type Node as RFNode,
  type NodeProps,
  Position,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from '@dagrejs/dagre';
import { toPng } from 'html-to-image';

import { KIND_SPECS, specForKind } from '../../kinds';
import { api } from '../../api';
import { GraphShareCardModal } from './GraphShareCard';

// ── data shapes mirror /api/projects/:pid/graph response ────────────────

interface GraphNode {
  id: string;
  kind: string;
  title: string | null;
  stale: boolean;
  archived: boolean;
  author: string;
  /** doc/12 §3.5.3: implementation tracking state (proposed | building |
   *  built | drift_detected) or null when the card isn't tracked. */
  implementation_status: string | null;
}

interface GraphEdge {
  src: string;
  dst: string;
  type: string;
}

interface ProjectGraph {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

// ── layout ─────────────────────────────────────────────────────────────

// Per-node dimensions used by dagre. Reactflow's default node is ~150x40 —
// our custom node is taller (title + kind chip) so we bump height.
const NODE_W = 200;
const NODE_H = 70;

/**
 * Compute (x, y) for each node via dagre's hierarchical layout. Direction
 * 'LR' (left-to-right) reads naturally for decision lineage chains
 * (observations → statements → decisions reading left to right). PR-A4b
 * adds a 'TB' (top-to-bottom) toggle for projects that read better
 * vertically (timeline-like sequences).
 */
function layoutWithDagre(nodes: GraphNode[], edges: GraphEdge[], rankdir: 'LR' | 'TB') {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir, nodesep: 50, ranksep: 90, marginx: 20, marginy: 20 });
  for (const n of nodes) g.setNode(n.id, { width: NODE_W, height: NODE_H });
  // Edges that reference cards outside the node set (archived / deleted)
  // would crash dagre; filter to known ids only.
  const ids = new Set(nodes.map((n) => n.id));
  for (const e of edges) {
    if (ids.has(e.src) && ids.has(e.dst)) {
      g.setEdge(e.src, e.dst);
    }
  }
  dagre.layout(g);
  const positions: Record<string, { x: number; y: number }> = {};
  for (const n of nodes) {
    const p = g.node(n.id);
    positions[n.id] = { x: p.x - NODE_W / 2, y: p.y - NODE_H / 2 };
  }
  return positions;
}

// ── custom node ────────────────────────────────────────────────────────

/**
 * Hover state lives in context so changes don't invalidate the rfNodes
 * array — reactflow keeps the same node identity, and only the affected
 * CardNode components re-render. Without this, hovering causes every
 * node to remount (visible as flicker on small graphs).
 */
interface GraphHoverState {
  hoverId: string | null;
  neighbors: Set<string> | null;
}
const GraphHoverCtx = createContext<GraphHoverState>({ hoverId: null, neighbors: null });

/**
 * PR-A4c v2: graph-specific palette. Diverges from KIND_SPECS (which lives
 * in kinds.ts and powers CardRow / CardPeek) because:
 *   - In list view a card sits among prose; gray-on-gray is fine.
 *   - In the graph 30-100 nodes compete for attention. Decisions need to
 *     POP. We borrow the V4 mock's warmer treatment: amber-soft fill +
 *     amber border on decisions, paper-2 + ink-3 on observations, etc.
 * Keeping it inline-local means CardRow keeps its current quiet palette.
 */
function graphPaletteFor(kind: string): {
  bg: string;
  border: string;
  titleColor: string;
} {
  switch (kind) {
    case 'decision':
      return { bg: 'var(--amber-soft)', border: 'var(--amber)', titleColor: 'var(--ink-0)' };
    case 'statement':
      return { bg: 'white', border: 'var(--ink-2)', titleColor: 'var(--ink-0)' };
    case 'observation':
      return { bg: 'var(--paper-2)', border: 'var(--ink-3)', titleColor: 'var(--ink-1)' };
    case 'question':
      return { bg: 'var(--amber-soft)', border: 'var(--amber)', titleColor: 'var(--ink-0)' };
    case 'gap':
      return { bg: 'var(--red-soft)', border: 'var(--red)', titleColor: 'var(--ink-0)' };
    case 'note':
      return { bg: 'var(--paper-0)', border: 'var(--ink-4)', titleColor: 'var(--ink-2)' };
    case 'dead_end':
      return { bg: 'var(--paper-2)', border: 'var(--ink-3)', titleColor: 'var(--ink-3)' };
    case 'pivot':
      return { bg: 'var(--amber-soft)', border: 'var(--amber)', titleColor: 'var(--ink-0)' };
    default:
      return { bg: 'white', border: 'var(--rule)', titleColor: 'var(--ink-2)' };
  }
}

/**
 * Render a card as a styled box. Reads the same KIND_SPECS as CardRow so
 * the visual vocabulary stays consistent (dead_end struck-through, pivot
 * amber, gap amber-highlight, etc.). Reactflow requires explicit Handles
 * for source / target sides — without them edges won't anchor.
 */
function CardNode({ data, selected }: NodeProps<{
  node: GraphNode;
  isAnchor: boolean;
  layoutDir: 'LR' | 'TB';
}>) {
  const { node, isAnchor, layoutDir } = data;
  const { hoverId, neighbors } = useContext(GraphHoverCtx);
  const highlighted = neighbors?.has(node.id) ?? false;
  const dimmed = neighbors != null && !highlighted && node.id !== hoverId;
  const palette = graphPaletteFor(node.kind);
  const spec = specForKind(node.kind);
  const isDeadEnd = node.kind === 'dead_end';
  // doc/12 §3.5.3: drift outranks every other border treatment — it's the
  // one state that demands action ("the code moved under this card").
  const isDrift = node.implementation_status === 'drift_detected';
  const isBuilt = node.implementation_status === 'built';

  // Drift → red border; anchor → green border (V4 mock); else the kind's
  // own border color. Hover/select doesn't change border *width* — only
  // color — so the box dimensions stay constant and there's no per-pixel
  // flicker on hover.
  const borderColor = isDrift
    ? 'var(--red)'
    : isAnchor
      ? 'var(--green)'
      : (selected || highlighted ? palette.titleColor : palette.border);

  return (
    <div
      style={{
        width: NODE_W,
        height: NODE_H,
        padding: '10px 12px',
        borderRadius: 8,
        background: palette.bg,
        // Border always 3px to keep box dimensions stable across hover /
        // anchor state changes. Non-emphasized states paint a 2px inset
        // shadow in the bg color so the visible stroke reads ~1px while
        // the box itself stays the same size.
        border: `3px solid ${borderColor}`,
        boxShadow: (() => {
          const layers: string[] = [];
          // Stale = "depends-on changed under this card, needs review".
          // Stronger amber wash than before (0.12 → 0.22) since we no
          // longer use strikethrough — wash is the only signal now.
          if (node.stale) layers.push('inset 0 0 0 9999px rgba(184, 105, 26, 0.22)');
          if (!isAnchor && !selected && !highlighted && !isDrift) {
            layers.push(`inset 0 0 0 2px ${palette.bg}`);
          }
          return layers.length > 0 ? layers.join(', ') : 'none';
        })(),
        opacity: dimmed ? 0.35 : (node.archived ? 0.55 : 1),
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'opacity 120ms, border-color 120ms',
      }}
    >
      <Handle
        type="target"
        position={layoutDir === 'TB' ? Position.Top : Position.Left}
        style={{ opacity: 0 }}
      />
      <Handle
        type="source"
        position={layoutDir === 'TB' ? Position.Bottom : Position.Right}
        style={{ opacity: 0 }}
      />
      <div
        className="mono"
        style={{
          fontSize: 10,
          color: palette.border,
          fontWeight: 700,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <span>{spec.glyph ?? '·'} {spec.label ?? node.kind}</span>
        <span style={{ color: 'var(--ink-3)', fontWeight: 500 }}>{node.id}</span>
        {/* Right-aligned badge cluster. Stale badge sits inline with the
            kind label so it reads as "this needs review" without conflating
            with dead_end strike-through. doc/12 §3.5.3 adds the
            implementation chips: built = green (the mark-built payoff
            moment), drift = red (matches the border). building/proposed
            stay chip-less here — the full four-state story lives in
            CardPeek's timeline. */}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 3 }}>
          {node.stale && !isDeadEnd && (
            <span
              title="Stale — a card this depends on changed; review and acknowledge"
              style={{
                fontSize: 9,
                padding: '0 4px',
                borderRadius: 2,
                background: 'var(--amber)',
                color: 'white',
                fontWeight: 700,
                letterSpacing: '.05em',
              }}
            >
              ⚠ STALE
            </span>
          )}
          {isBuilt && (
            <span
              title="Built — implementation landed and matches this card"
              style={{
                fontSize: 9,
                padding: '0 4px',
                borderRadius: 2,
                background: 'var(--green)',
                color: 'white',
                fontWeight: 700,
                letterSpacing: '.05em',
              }}
            >
              ✓ BUILT
            </span>
          )}
          {isDrift && (
            <span
              title="Drift — this card changed after its implementation was marked built"
              style={{
                fontSize: 9,
                padding: '0 4px',
                borderRadius: 2,
                background: 'var(--red)',
                color: 'white',
                fontWeight: 700,
                letterSpacing: '.05em',
              }}
            >
              ⚠ DRIFT
            </span>
          )}
        </span>
      </div>
      <div
        style={{
          fontSize: 12.5,
          lineHeight: 1.35,
          color: palette.titleColor,
          fontWeight: node.kind === 'decision' ? 700 : 500,
          // Strikethrough is now reserved for dead_end (which IS killed).
          // Stale uses the amber inset wash + ⚠ badge above; the title
          // stays readable so the user can scan the card without squinting.
          textDecoration: isDeadEnd ? 'line-through' : 'none',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {node.title ?? <em style={{ color: 'var(--ink-3)' }}>(untitled)</em>}
      </div>
    </div>
  );
}

// ── region node (TOPO Layer 0) ─────────────────────────────────────────

const REGION_W = 240;
const REGION_H = 116;

/**
 * PR-A4d: a "region" — one anchor's cone packaged as a single big box for
 * the TOPO overview. Bigger than CardNode (240x116 vs 200x70) and styled
 * to read like a section of a wiki rather than an individual fact. Clicking
 * the box drills into MAP mode scoped to that anchor.
 */
function RegionNode({ data }: NodeProps<{
  summary: AnchorSummary;
  layoutDir: 'LR' | 'TB';
}>) {
  const { summary, layoutDir } = data;
  const { anchor, cone, depth, staleCount } = summary;
  const palette = graphPaletteFor(anchor.kind);
  const spec = specForKind(anchor.kind);
  const cardCount = cone.size;
  return (
    <div
      style={{
        width: REGION_W,
        height: REGION_H,
        padding: '12px 14px',
        borderRadius: 10,
        background: palette.bg,
        border: `3px solid ${palette.border}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        cursor: 'pointer',
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      <Handle
        type="target"
        position={layoutDir === 'TB' ? Position.Top : Position.Left}
        style={{ opacity: 0 }}
      />
      <Handle
        type="source"
        position={layoutDir === 'TB' ? Position.Bottom : Position.Right}
        style={{ opacity: 0 }}
      />
      {/* Kind label + id */}
      <div
        className="mono"
        style={{
          fontSize: 10,
          color: palette.border,
          fontWeight: 700,
          letterSpacing: '.12em',
          textTransform: 'uppercase',
          display: 'flex',
          alignItems: 'center',
          gap: 5,
        }}
      >
        <span>{spec.glyph ?? '·'} {spec.label ?? anchor.kind}</span>
        <span style={{ color: 'var(--ink-3)', fontWeight: 500 }}>{anchor.id}</span>
      </div>
      {/* Anchor title — bold, 2-line clamp */}
      <div
        style={{
          fontSize: 13,
          lineHeight: 1.3,
          fontWeight: 700,
          color: palette.titleColor,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {anchor.title ?? <em style={{ color: 'var(--ink-3)' }}>(untitled)</em>}
      </div>
      {/* Stats row */}
      <div
        className="mono"
        style={{
          fontSize: 10,
          color: 'var(--ink-2)',
          letterSpacing: '.04em',
          marginTop: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <span><strong style={{ color: 'var(--ink-0)' }}>{cardCount}</strong> cards</span>
        <span style={{ color: 'var(--rule-s)' }}>·</span>
        <span>depth {depth}</span>
        {staleCount > 0 && (
          <>
            <span style={{ color: 'var(--rule-s)' }}>·</span>
            <span
              title={`${staleCount} card${staleCount === 1 ? '' : 's'} in this region are stale and need review`}
              style={{
                color: 'var(--amber)',
                fontWeight: 700,
              }}
            >
              ⚠ {staleCount} stale
            </span>
          </>
        )}
      </div>
    </div>
  );
}

const nodeTypes = { card: CardNode, region: RegionNode };

// ── edge styling ────────────────────────────────────────────────────────

interface EdgeStyle {
  stroke: string;
  strokeWidth: number;
  strokeDasharray?: string;
}

function edgeStyleFor(type: string, highlighted: boolean): EdgeStyle {
  // depends-on drives stale propagation, so it gets the heaviest stroke.
  // contradicts is the only edge type the graph explicitly warns about.
  const base = (() => {
    switch (type) {
      case 'depends-on':
        return { stroke: 'var(--ink-1)', strokeWidth: 2 };
      case 'supports':
        return { stroke: 'var(--ink-2)', strokeWidth: 1 };
      case 'contradicts':
        return { stroke: 'var(--red)', strokeWidth: 1.5, strokeDasharray: '6 4' };
      case 'derived-from':
        return { stroke: 'var(--ink-3)', strokeWidth: 1, strokeDasharray: '3 3' };
      default:
        return { stroke: 'var(--ink-3)', strokeWidth: 1 };
    }
  })();
  if (highlighted) {
    return { ...base, strokeWidth: base.strokeWidth + 1 };
  }
  return base;
}

/**
 * doc/11 PR-A4b: BFS the N-hop neighborhood of `focusId` over both edge
 * directions. Used by focus mode to dim everything outside the radius —
 * "give me this decision's evidence + alternatives + impacted depends-on
 * cards" use case.
 */
function nHopNeighborhood(
  focusId: string,
  edges: GraphEdge[],
  depth: number,
): Set<string> {
  const visited = new Set<string>([focusId]);
  let frontier = new Set<string>([focusId]);
  for (let d = 0; d < depth; d++) {
    const next = new Set<string>();
    for (const e of edges) {
      if (frontier.has(e.src) && !visited.has(e.dst)) next.add(e.dst);
      if (frontier.has(e.dst) && !visited.has(e.src)) next.add(e.src);
    }
    if (next.size === 0) break;
    for (const id of next) visited.add(id);
    frontier = next;
  }
  return visited;
}

/**
 * PR-A4c (V4 Map / V10 Pitch): walk the upstream cone of `anchorId`
 * following `depends-on` edges in the outgoing direction. In our DAG
 * `A -depends-on-> B` means A needs B; B is upstream of A. So starting
 * from an anchor (a "current decision" that depends on things), BFS
 * along OUTgoing edges reaches its dependencies → its evidence chain.
 *
 * Restricts to `depends-on` edges only because supports / derived-from
 * / connects create cycles + density that defeat the spine narrative.
 */
function upstreamCone(
  anchorId: string,
  edges: GraphEdge[],
  depth: number,
): Set<string> {
  const visited = new Set<string>([anchorId]);
  let frontier = new Set<string>([anchorId]);
  for (let d = 0; d < depth; d++) {
    const next = new Set<string>();
    for (const e of edges) {
      if (e.type !== 'depends-on') continue;
      if (frontier.has(e.src) && !visited.has(e.dst)) next.add(e.dst);
    }
    if (next.size === 0) break;
    for (const id of next) visited.add(id);
    frontier = next;
  }
  return visited;
}

/**
 * PR-A4c: "current decision" anchors per V4 spec. In our depends-on DAG
 * a card with in_deg=0 (nothing depends on it yet) AND out_deg>0 (it
 * depends on at least one thing) is a "latest decision" — the project's
 * current frontier. Returned sorted by upstream cone size descending so
 * Map/Pitch modes pick the cards with the deepest narrative behind them.
 *
 * Note: the spec calls these "leaves" colloquially but in DAG terms
 * they're roots of the dependency tree (downstream-most). The naming
 * convention here matches user mental model: "the latest stuff sits on
 * top, with its history below."
 */
function computeAnchors(
  nodes: GraphNode[],
  edges: GraphEdge[],
): GraphNode[] {
  const inDeg = new Map<string, number>();
  const outDeg = new Map<string, number>();
  for (const e of edges) {
    if (e.type !== 'depends-on') continue;
    outDeg.set(e.src, (outDeg.get(e.src) ?? 0) + 1);
    inDeg.set(e.dst, (inDeg.get(e.dst) ?? 0) + 1);
  }
  const candidates = nodes.filter(
    (n) =>
      !n.archived &&
      (inDeg.get(n.id) ?? 0) === 0 &&
      (outDeg.get(n.id) ?? 0) > 0,
  );
  const coneSize = new Map<string, number>();
  for (const c of candidates) {
    // Use a relatively deep BFS (4) so cone-size ranking captures the
    // total reach, not just immediate dependencies.
    coneSize.set(c.id, upstreamCone(c.id, edges, 4).size);
  }
  candidates.sort((a, b) => (coneSize.get(b.id) ?? 0) - (coneSize.get(a.id) ?? 0));
  return candidates;
}

/**
 * PR-A4d (TOPO Layer 0): per-anchor summary stats. Used to render the
 * region boxes in TOPO mode — each anchor becomes ONE box showing its
 * cone size, depth, and stale count. Computed once per (graph, depth)
 * change since cone walks aren't free.
 */
interface AnchorSummary {
  anchor: GraphNode;
  cone: Set<string>;
  depth: number;
  staleCount: number;
}

function summarizeAnchors(
  anchors: GraphNode[],
  nodes: GraphNode[],
  edges: GraphEdge[],
  coneDepth: number,
): AnchorSummary[] {
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  return anchors.map((a) => {
    // BFS along outgoing depends-on to compute the cone + actual depth
    // reached. We do this manually here (rather than calling upstreamCone)
    // because we want the depth as a return value too.
    const visited = new Map<string, number>([[a.id, 0]]);
    let frontier = new Set<string>([a.id]);
    let maxDepth = 0;
    for (let d = 0; d < coneDepth; d++) {
      const next = new Set<string>();
      for (const e of edges) {
        if (e.type !== 'depends-on') continue;
        if (frontier.has(e.src) && !visited.has(e.dst)) next.add(e.dst);
      }
      if (next.size === 0) break;
      for (const id of next) visited.set(id, d + 1);
      frontier = next;
      maxDepth = d + 1;
    }
    const coneSet = new Set(visited.keys());
    let staleCount = 0;
    for (const id of coneSet) {
      if (nodeById.get(id)?.stale) staleCount++;
    }
    return { anchor: a, cone: coneSet, depth: maxDepth, staleCount };
  });
}

/**
 * PR-A4d: pairwise shared-upstream count. For two anchor cones A and B,
 * how many cards do they have in common (excluding the anchors themselves)?
 * This is the "coupling" signal between subsystems — boxes get connected
 * in TOPO when their cones overlap by ≥1 upstream card.
 */
function computeRegionOverlap(
  summaries: AnchorSummary[],
): { src: string; dst: string; shared: number }[] {
  const out: { src: string; dst: string; shared: number }[] = [];
  for (let i = 0; i < summaries.length; i++) {
    for (let j = i + 1; j < summaries.length; j++) {
      const a = summaries[i];
      const b = summaries[j];
      let shared = 0;
      for (const id of a.cone) {
        if (id === a.anchor.id || id === b.anchor.id) continue;
        if (b.cone.has(id)) shared++;
      }
      if (shared > 0) {
        out.push({ src: a.anchor.id, dst: b.anchor.id, shared });
      }
    }
  }
  return out;
}

/**
 * PR-A4c (V10 Pitch): lay out each anchor's cone in its own panel and
 * offset the panels horizontally so they don't overlap. Edges that cross
 * panels (an upstream card cited by multiple anchors) get rendered in
 * just one panel — the first one — to keep each panel a self-contained
 * narrative.
 */
function layoutPanels(
  anchorIds: string[],
  cones: Map<string, Set<string>>,
  nodes: GraphNode[],
  edges: GraphEdge[],
): { positions: Record<string, { x: number; y: number }>; edgesInPanel: Set<string> } {
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const placed = new Set<string>();
  const allPositions: Record<string, { x: number; y: number }> = {};
  const edgesInPanel = new Set<string>(); // key = `${src}|${dst}|${type}`
  const PANEL_GAP = 80;
  let xOffset = 0;
  for (const aid of anchorIds) {
    const coneIds = cones.get(aid);
    if (!coneIds) continue;
    // Only include nodes not yet placed by an earlier panel — keeps each
    // panel self-contained. The anchor itself is always placed first
    // panel-it-belongs-to even if it appears in multiple cones (sorted
    // by cone size means the largest narrative gets it first).
    const panelNodeIds = [...coneIds].filter((id) => !placed.has(id));
    const panelNodes = panelNodeIds
      .map((id) => nodeById.get(id))
      .filter((n): n is GraphNode => n !== undefined);
    if (panelNodes.length === 0) continue;
    const panelEdges = edges.filter(
      (e) =>
        e.type === 'depends-on' &&
        panelNodeIds.includes(e.src) &&
        panelNodeIds.includes(e.dst),
    );
    const positions = layoutWithDagre(panelNodes, panelEdges, 'TB');
    // Compute panel bounds for offsetting.
    const xs = Object.values(positions).map((p) => p.x);
    const minX = xs.length ? Math.min(...xs) : 0;
    const maxX = xs.length ? Math.max(...xs) : 0;
    for (const id of panelNodeIds) {
      const p = positions[id];
      if (!p) continue;
      allPositions[id] = { x: p.x - minX + xOffset, y: p.y };
      placed.add(id);
    }
    for (const e of panelEdges) {
      edgesInPanel.add(`${e.src}|${e.dst}|${e.type}`);
    }
    xOffset += (maxX - minX) + NODE_W + PANEL_GAP;
  }
  return { positions: allPositions, edgesInPanel };
}

// ── main component ────────────────────────────────────────────────────

export interface CardGraphProps {
  projectId: string;
  /** Project display name — printed on the §3.2.2 share card. Falls back
   *  to a generic label when the parent doesn't know it. */
  projectName?: string | null;
  onPeek: (cardId: string) => void;
  /** Switch the cards panel out of graph view into the flat list / frame
   *  view. Rendered as a discreet "≡ List" button in GraphControls so the
   *  user can fall back to the older interface without leaving the panel. */
  onLeaveGraph?: () => void;
}

export function CardGraph(props: CardGraphProps) {
  // ReactFlowProvider is required so the export button can use
  // useReactFlow().getNodes() to compute the viewport bounds.
  return (
    <ReactFlowProvider>
      <CardGraphInner {...props} />
    </ReactFlowProvider>
  );
}

function CardGraphInner({ projectId, projectName, onPeek, onLeaveGraph }: CardGraphProps) {
  const [graph, setGraph] = useState<ProjectGraph | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const fetchedFor = useRef<string | null>(null);

  // PR-A4d: 4 modes for 3 layers (TOPO = overview / MAP = subsystem /
  // DEEP = single-card lineage / PITCH = export). TOPO is the default
  // landing — "show me the shape of my project at a glance."
  const [viewMode, setViewMode] = useState<'topo' | 'map' | 'deep' | 'pitch'>('topo');
  // Which anchor is MAP scoped to. Set when user clicks a TOPO region.
  // null = fall back to top-1 anchor by cone size (so entering MAP directly
  // still shows something useful).
  const [mapAnchorId, setMapAnchorId] = useState<string | null>(null);
  // PITCH-only: how many anchors to render as panels. Renamed from
  // mapAnchorCount since MAP no longer uses it.
  const [pitchCount, setPitchCount] = useState<number>(3); // 1 | 3 | 5 | all
  const [focusId, setFocusId] = useState<string | null>(null);
  const [focusDepth, setFocusDepth] = useState(2);
  const [kindFilter, setKindFilter] = useState<Set<string>>(new Set());
  // PR-A4c: default TB for all four modes (matches V4/V5/V10 mocks which
  // read top-down: current decisions on top, evidence underneath).
  const [layoutDir, setLayoutDir] = useState<'LR' | 'TB'>('TB');
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch graph once per projectId. The graph endpoint is < 100KB for
  // the Phase B ceiling (< 1000 cards), so a fresh fetch per project
  // open is fine; no caching layer yet. SSE-driven refresh (re-fetch on
  // CardWritten / CardUpdated) is a follow-up.
  useEffect(() => {
    if (fetchedFor.current === projectId) return;
    fetchedFor.current = projectId;
    setError(null);
    setGraph(null);
    setFocusId(null);
    setKindFilter(new Set());
    setViewMode('topo');
    setMapAnchorId(null);
    setPitchCount(3);
    fetch(`/api/projects/${projectId}/graph`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`graph fetch failed: HTTP ${r.status}`);
        return r.json() as Promise<ProjectGraph>;
      })
      .then(setGraph)
      .catch((e) => setError(String(e)));
  }, [projectId]);

  // PR-A4d ESC navigates UP one layer:
  //   DEEP → MAP (drop focusId)
  //   MAP  → TOPO (drop mapAnchorId)
  //   TOPO → no-op (already at the top)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (viewMode === 'deep') {
        setFocusId(null);
        setViewMode('map');
      } else if (viewMode === 'map') {
        setMapAnchorId(null);
        setViewMode('topo');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewMode]);

  // PR-A4c: compute "current decision" anchors once per graph payload.
  // Used by all four modes (TOPO renders boxes, MAP scopes to one, DEEP
  // falls back to top-1 if no focusId, PITCH picks top N).
  const allAnchors = useMemo(
    () => (graph ? computeAnchors(graph.nodes, graph.edges) : []),
    [graph],
  );

  // PR-A4d: per-anchor summaries (cone + depth + stale count) for TOPO
  // boxes. Computed across ALL anchors so the overview is complete.
  const anchorSummaries = useMemo(() => {
    if (!graph) return [];
    return summarizeAnchors(allAnchors, graph.nodes, graph.edges, focusDepth);
  }, [graph, allAnchors, focusDepth]);

  // Pairwise overlap between anchor cones → TOPO inter-region edges.
  const regionOverlaps = useMemo(
    () => computeRegionOverlap(anchorSummaries),
    [anchorSummaries],
  );

  // PR-A4c mode-aware filtering. Each mode produces its own (nodes, edges,
  // positions) triple plus optional metadata (anchor highlights, panel
  // boundaries). kindFilter applies on top of all modes EXCEPT topo.
  const {
    topoSummaries,
    topoOverlaps,
    filteredNodes,
    filteredEdges,
    positions,
    anchorSet,
  } = useMemo(() => {
    const empty = {
      topoSummaries: [] as AnchorSummary[],
      topoOverlaps: [] as { src: string; dst: string; shared: number }[],
      filteredNodes: [] as GraphNode[],
      filteredEdges: [] as GraphEdge[],
      positions: {} as Record<string, { x: number; y: number }>,
      anchorSet: new Set<string>(),
    };
    if (!graph) return empty;

    let nodes: GraphNode[] = [];
    let edges: GraphEdge[] = [];
    let pos: Record<string, { x: number; y: number }> = {};
    let aSet = new Set<string>();

    if (viewMode === 'topo') {
      // TOPO Layer 0 — anchor boxes laid out as a meta-graph. Inter-region
      // edges are computed separately (regionOverlaps); nodes/edges here
      // are zero because TOPO uses a different node type ('region') and we
      // build rfNodes / rfEdges for it on the JSX side.
      return {
        topoSummaries: anchorSummaries,
        topoOverlaps: regionOverlaps,
        filteredNodes: [],
        filteredEdges: [],
        positions: {},
        anchorSet: new Set(anchorSummaries.map((s) => s.anchor.id)),
      };
    } else if (viewMode === 'map') {
      // MAP Layer 1 — single anchor's cone. Default to top-1 if user
      // entered MAP without clicking through TOPO (e.g., direct mode tab).
      const anchorId = mapAnchorId ?? allAnchors[0]?.id ?? null;
      if (anchorId) {
        const cone = upstreamCone(anchorId, graph.edges, focusDepth);
        nodes = graph.nodes.filter((n) => cone.has(n.id));
        edges = graph.edges.filter(
          (e) => e.type === 'depends-on' && cone.has(e.src) && cone.has(e.dst),
        );
        aSet = new Set([anchorId]);
        pos = layoutWithDagre(nodes, edges, layoutDir);
      }
    } else if (viewMode === 'deep') {
      // DEEP Layer 2 — single card bidirectional N-hop.
      const anchor = focusId ?? mapAnchorId ?? allAnchors[0]?.id ?? null;
      if (anchor) {
        const hood = nHopNeighborhood(anchor, graph.edges, focusDepth);
        nodes = graph.nodes.filter((n) => hood.has(n.id));
        edges = graph.edges.filter((e) => hood.has(e.src) && hood.has(e.dst));
        aSet = new Set([anchor]);
        pos = layoutWithDagre(nodes, edges, layoutDir);
      }
    } else if (viewMode === 'pitch') {
      // V10 small multiples — top N anchors as separate panels.
      const topIds = allAnchors.slice(0, pitchCount).map((a) => a.id);
      const cones = new Map<string, Set<string>>();
      for (const id of topIds) {
        cones.set(id, upstreamCone(id, graph.edges, focusDepth));
      }
      const { positions: panelPos, edgesInPanel } = layoutPanels(
        topIds, cones, graph.nodes, graph.edges,
      );
      const placedIds = new Set(Object.keys(panelPos));
      nodes = graph.nodes.filter((n) => placedIds.has(n.id));
      edges = graph.edges.filter(
        (e) => e.type === 'depends-on' && edgesInPanel.has(`${e.src}|${e.dst}|${e.type}`),
      );
      aSet = new Set(topIds);
      pos = panelPos;
    }

    // Apply kind filter on top (MAP / DEEP / PITCH only; TOPO returned early)
    if (kindFilter.size > 0) {
      const keep = new Set(nodes.filter((n) => kindFilter.has(n.kind)).map((n) => n.id));
      nodes = nodes.filter((n) => keep.has(n.id));
      edges = edges.filter((e) => keep.has(e.src) && keep.has(e.dst));
    }

    return {
      topoSummaries: [],
      topoOverlaps: [],
      filteredNodes: nodes,
      filteredEdges: edges,
      positions: pos,
      anchorSet: aSet,
    };
  }, [graph, viewMode, allAnchors, anchorSummaries, regionOverlaps, mapAnchorId, focusId, focusDepth, pitchCount, kindFilter, layoutDir]);

  // Hover neighbors: only computed when NOT in focus mode (focus already
  // dims everything outside the N-hop set; a second dim layer is noise).
  const neighbors = useMemo(() => {
    if (focusId) return null;
    if (!graph || !hoverId) return null;
    const set = new Set<string>([hoverId]);
    for (const e of graph.edges) {
      if (e.src === hoverId) set.add(e.dst);
      if (e.dst === hoverId) set.add(e.src);
    }
    return set;
  }, [graph, hoverId, focusId]);

  // PR-A4d: in TOPO mode rfNodes are region boxes (one per anchor); in
  // MAP / DEEP / PITCH they are individual cards. Two node types
  // ('region' / 'card') are registered in `nodeTypes`.
  // Note: hoverId / neighbors deliberately NOT in this memo's deps. Hover
  // state flows through GraphHoverCtx so CardNode re-renders without
  // re-creating the rfNodes array (the visible flicker fix from PR-A4c v2).
  const rfNodes: RFNode[] = useMemo(() => {
    if (viewMode === 'topo') {
      // TOPO grid layout: sort anchors by cone size desc, lay out in a
      // 3-column grid. Edges (overlaps) are decorative so layout doesn't
      // need to respect them. 3 cols fits 240px-wide boxes nicely; 4 if
      // the project gets really crowded (>15 anchors).
      const cols = topoSummaries.length > 12 ? 4 : 3;
      const GAP_X = 50;
      const GAP_Y = 40;
      return topoSummaries.map((summary, i) => ({
        id: summary.anchor.id,
        type: 'region',
        position: {
          x: (i % cols) * (REGION_W + GAP_X),
          y: Math.floor(i / cols) * (REGION_H + GAP_Y),
        },
        targetPosition: Position.Top,
        sourcePosition: Position.Bottom,
        data: { summary, layoutDir: 'TB' as const },
      }));
    }
    return filteredNodes.map((n) => ({
      id: n.id,
      type: 'card',
      position: positions[n.id] ?? { x: 0, y: 0 },
      targetPosition: layoutDir === 'TB' ? Position.Top : Position.Left,
      sourcePosition: layoutDir === 'TB' ? Position.Bottom : Position.Right,
      data: {
        node: n,
        isAnchor: anchorSet.has(n.id),
        layoutDir,
      },
    }));
  }, [viewMode, topoSummaries, filteredNodes, positions, anchorSet, layoutDir]);

  const hoverState = useMemo<GraphHoverState>(
    () => ({ hoverId, neighbors }),
    [hoverId, neighbors],
  );

  const rfEdges: RFEdge[] = useMemo(() => {
    if (viewMode === 'topo') {
      // PR-A4d follow-up: TOPO no longer renders inter-region "shares N"
      // edges. With a grid layout the bezier curves crossed each other
      // and the boxes randomly, producing visual mess rather than signal.
      // The coupling info still computed (regionOverlaps) — we can
      // surface it later via hover or a "show coupling" toggle without
      // permanently polluting the overview.
      return [];
    }
    return filteredEdges.map((e, i) => {
      const incident = hoverId === e.src || hoverId === e.dst;
      const dimmed = neighbors && !incident;
      const style = edgeStyleFor(e.type, !!incident);
      return {
        id: `e-${i}-${e.src}-${e.dst}-${e.type}`,
        source: e.src,
        target: e.dst,
        label: e.type,
        labelStyle: {
          fontSize: 9,
          fill: 'var(--ink-3)',
          fontFamily: 'JetBrains Mono, monospace',
        },
        labelBgStyle: { fill: 'var(--paper-0)' },
        labelBgPadding: [2, 4],
        style: {
          ...style,
          opacity: dimmed ? 0.2 : 1,
        },
      };
    });
  }, [viewMode, topoOverlaps, filteredEdges, neighbors, hoverId]);

  const toggleKind = useCallback((kind: string) => {
    setKindFilter((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  }, []);

  const exportPng = useCallback(async () => {
    const viewport = containerRef.current?.querySelector('.react-flow__viewport');
    if (!(viewport instanceof HTMLElement)) return;
    try {
      const dataUrl = await toPng(viewport, {
        backgroundColor: '#FCFBF9',  // matches --paper-0
        pixelRatio: 2,
        cacheBust: true,
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `draftlize-graph-${projectId}-${Date.now()}.png`;
      a.click();
    } catch (e) {
      console.error('graph PNG export failed', e);
    }
  }, [projectId]);

  // Full-project ZIP export (cards + deletions + history + threads). The
  // list-view footer used to be the only entry point, but graph view is
  // the default and its "≡ List" escape hatch is disabled — so without a
  // button here the export was unreachable. Reuses api.exportProject,
  // which downloads the server-built ZIP regardless of the current filter.
  const [exporting, setExporting] = useState(false);
  const exportZip = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await api.exportProject(projectId);
    } catch (e) {
      console.error('project export failed', e);
      // eslint-disable-next-line no-alert
      alert(`Export failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setExporting(false);
    }
  }, [projectId, exporting]);

  // doc/12 §3.2.2: the Share button opens the snapshot-card modal —
  // share-link creation (§3.2.1), screenshot, QR, and canvas composition
  // all live inside GraphShareCardModal. Copy-link stays one click away
  // in the modal, so the old copy-to-clipboard shortcut isn't missed.
  const [shareCardOpen, setShareCardOpen] = useState(false);

  if (error) {
    return (
      <div style={{ padding: 20, color: 'var(--red)', fontSize: 13 }}>
        Graph failed to load: {error}
      </div>
    );
  }

  if (!graph) {
    return (
      <div style={{ padding: 20, color: 'var(--ink-3)', fontSize: 13 }}>
        Loading graph…
      </div>
    );
  }

  if (graph.nodes.length === 0) {
    return (
      <div style={{ padding: 20, color: 'var(--ink-3)', fontSize: 13 }}>
        No cards yet — graph will populate as the project grows.
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {shareCardOpen && (
        <GraphShareCardModal
          projectId={projectId}
          projectName={projectName || 'Draftlize project'}
          cardCount={graph.nodes.length}
          decisionCount={graph.nodes.filter((n) => n.kind === 'decision').length}
          graphEl={containerRef.current}
          onClose={() => setShareCardOpen(false)}
        />
      )}
      <GraphControls
        viewMode={viewMode}
        setViewMode={setViewMode}
        pitchCount={pitchCount}
        setPitchCount={setPitchCount}
        anchorTotal={allAnchors.length}
        kindFilter={kindFilter}
        toggleKind={toggleKind}
        clearKinds={() => setKindFilter(new Set())}
        layoutDir={layoutDir}
        setLayoutDir={setLayoutDir}
        focusId={focusId}
        focusDepth={focusDepth}
        setFocusDepth={setFocusDepth}
        mapAnchorTitle={
          mapAnchorId
            ? allAnchors.find((a) => a.id === mapAnchorId)?.title ?? null
            : null
        }
        clearFocus={() => {
          setFocusId(null);
          if (viewMode === 'deep') setViewMode('map');
        }}
        onExportPng={exportPng}
        onExport={exportZip}
        exporting={exporting}
        onShare={() => setShareCardOpen(true)}
        onLeaveGraph={onLeaveGraph}
        visibleCount={
          viewMode === 'topo' ? topoSummaries.length : filteredNodes.length
        }
        totalCount={viewMode === 'topo' ? allAnchors.length : graph.nodes.length}
        staleCount={
          viewMode === 'topo'
            ? topoSummaries.reduce((sum, s) => sum + s.staleCount, 0)
            : filteredNodes.filter((n) => n.stale).length
        }
      />
      <div ref={containerRef} style={{ flex: 1, background: 'var(--paper-0)', position: 'relative' }}>
       {/* PR-A4c v2: floating affordance strip. Always-visible reminder of
           the (single click → peek / double click → drill / ESC → back)
           rules so the user doesn't have to remember mode-dependent
           behavior. Positioned bottom-left so it doesn't fight the
           reactflow Controls (also bottom-left default — reactflow Controls
           moves up to make room). */}
       <div
         style={{
           position: 'absolute',
           top: 8,
           left: 8,
           zIndex: 5,
           display: 'flex',
           alignItems: 'center',
           gap: 10,
           padding: '4px 10px',
           background: 'rgba(252, 251, 249, 0.92)',
           border: '1px solid var(--rule)',
           borderRadius: 4,
           fontSize: 9.5,
           color: 'var(--ink-3)',
           letterSpacing: '.04em',
           pointerEvents: 'none',
           backdropFilter: 'blur(2px)',
         }}
         className="mono"
       >
         {/* PR-A4d v2: unified hint copy — always CLICK = peek, DBL-CLICK
             = drill, ESC = back. Only the drill destination wording
             changes per layer so the user knows what they're entering. */}
         <span><strong style={{ color: 'var(--ink-1)', fontWeight: 700 }}>CLICK</strong> peek</span>
         <span style={{ color: 'var(--rule-s)' }}>·</span>
         <span>
           <strong style={{ color: 'var(--ink-1)', fontWeight: 700 }}>DBL-CLICK</strong>{' '}
           {viewMode === 'topo' && 'open subsystem'}
           {viewMode === 'map' && 'deep dive'}
           {viewMode === 'deep' && 're-focus'}
           {viewMode === 'pitch' && 'deep dive'}
         </span>
         {viewMode !== 'topo' && (
           <>
             <span style={{ color: 'var(--rule-s)' }}>·</span>
             <span><strong style={{ color: 'var(--ink-1)', fontWeight: 700 }}>ESC</strong> back</span>
           </>
         )}
       </div>
       <GraphHoverCtx.Provider value={hoverState}>
        <ReactFlow
          nodes={rfNodes}
          edges={rfEdges}
          nodeTypes={nodeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable
          // PR-A4d v3: reactflow's default double-click handler zooms the
          // canvas, which clobbered our onNodeDoubleClick (drill-in) intent.
          // Disable canvas-zoom-on-dblclick so dbl-click is exclusively for
          // node interaction. Users still zoom via +/- in Controls, scroll
          // wheel, and pinch.
          zoomOnDoubleClick={false}
          proOptions={{ hideAttribution: true }}
          fitView
          fitViewOptions={{ padding: 0.15 }}
          onNodeMouseEnter={(_, n) => setHoverId(n.id)}
          onNodeMouseLeave={() => setHoverId(null)}
          // PR-A4d v2: ONE rule across every mode + layer.
          //
          //   single click   → preview the card (CardPeek)
          //   double click   → drill one level deeper
          //   ESC            → go up one level
          //
          // In TOPO single-click on a region opens the anchor card's
          // CardPeek (the region IS that anchor). Double-click drills
          // one layer down to MAP. Drilling two layers (TOPO→DEEP) needs
          // two double-clicks — matches OS file-manager mental model.
          onNodeClick={(_, n) => onPeek(n.id)}
          onNodeDoubleClick={(_, n) => {
            if (viewMode === 'topo') {
              setMapAnchorId(n.id);
              setViewMode('map');
            } else {
              setFocusId(n.id);
              setViewMode('deep');
            }
          }}
        >
          <Background gap={20} size={1} color="var(--rule)" />
          <Controls showInteractive={false} />
        </ReactFlow>
       </GraphHoverCtx.Provider>
      </div>
    </div>
  );
}

/**
 * doc/11 PR-A4b/c/d control bar. 4 modes for 3 layers:
 *   TOPO  — Layer 0 overview (region boxes per anchor)
 *   MAP   — Layer 1 single-anchor cone
 *   DEEP  — Layer 2 single-card bidirectional N-hop
 *   PITCH — multi-panel export view
 * Top row: mode selector + mode-specific N selector + breadcrumb-y info.
 * Bottom row: kind filter chips + layout direction + focus-depth + PNG.
 */
function GraphControls({
  viewMode,
  setViewMode,
  pitchCount,
  setPitchCount,
  anchorTotal,
  kindFilter,
  toggleKind,
  clearKinds,
  layoutDir,
  setLayoutDir,
  focusId,
  focusDepth,
  setFocusDepth,
  mapAnchorTitle,
  clearFocus,
  onExportPng,
  onExport,
  exporting,
  onShare,
  onLeaveGraph,
  visibleCount,
  totalCount,
  staleCount,
}: {
  viewMode: 'topo' | 'map' | 'deep' | 'pitch';
  setViewMode: (m: 'topo' | 'map' | 'deep' | 'pitch') => void;
  pitchCount: number;
  setPitchCount: (n: number) => void;
  anchorTotal: number;
  kindFilter: Set<string>;
  toggleKind: (k: string) => void;
  clearKinds: () => void;
  layoutDir: 'LR' | 'TB';
  setLayoutDir: (d: 'LR' | 'TB') => void;
  focusId: string | null;
  focusDepth: number;
  setFocusDepth: (n: number) => void;
  mapAnchorTitle: string | null;
  clearFocus: () => void;
  onExportPng: () => void;
  onExport: () => void;
  exporting: boolean;
  onShare: () => void;
  onLeaveGraph?: () => void;
  visibleCount: number;
  totalCount: number;
  staleCount: number;
}) {
  const modeButton = (mode: 'topo' | 'map' | 'deep' | 'pitch', label: string, hint: string) => (
    <button
      key={mode}
      onClick={() => setViewMode(mode)}
      className="mono"
      title={hint}
      style={{
        fontSize: 10,
        padding: '4px 10px',
        border: `1px solid ${viewMode === mode ? 'var(--ink-0)' : 'var(--rule)'}`,
        borderRadius: 3,
        background: viewMode === mode ? 'var(--ink-0)' : 'white',
        color: viewMode === mode ? 'white' : 'var(--ink-2)',
        fontWeight: viewMode === mode ? 700 : 500,
        letterSpacing: '.06em',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );

  // PR-A4d follow-up v2: filter chips moved into a popover triggered
  // by a `▾ Filter (N)` button. Eight chips + the CLEAR button no
  // longer hog a second row; second row only appears when the popover
  // is open. Click-outside closes.
  const [filterOpen, setFilterOpen] = useState(false);
  const filterAnchorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!filterOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!filterAnchorRef.current?.contains(e.target as Node)) {
        setFilterOpen(false);
      }
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [filterOpen]);

  return (
    <div
      style={{
        background: 'var(--paper-1)',
        borderBottom: '1px solid var(--rule)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Single header row — modes + filter dropdown + actions.
          PR-A4d follow-up: minHeight (not height) so the row grows
          gracefully when narrow viewports force a second line; height
          would clip content. 45px floor matches the project's standard
          header height when content fits in one row. */}
      <div
        style={{
          minHeight: 45,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 12px',
          flexWrap: 'wrap',
          rowGap: 6,
        }}
      >
        {onLeaveGraph && (
          <button
            onClick={onLeaveGraph}
            className="mono"
            title="Switch to flat list / frame view"
            style={{
              fontSize: 10,
              padding: '4px 8px',
              border: '1px solid var(--rule)',
              borderRadius: 3,
              background: 'white',
              color: 'var(--ink-3)',
              letterSpacing: '.06em',
              cursor: 'pointer',
            }}
          >
            ≡ List
          </button>
        )}
        {modeButton('topo', '◳ TOPO', 'Project overview — anchor regions + coupling (Layer 0)')}
        {modeButton('map', '◐ MAP', 'One subsystem — single-anchor cone (Layer 1)')}
        {modeButton('deep', '✣ DEEP', 'Single-card bidirectional N-hop (Layer 2)')}
        {modeButton('pitch', '☷ PITCH', 'Multi-panel deck-ready export')}

        {/* Kind-filter popover trigger. Compacts 8 chips + CLEAR into one
            button on the main row. Counter chip on the right when filters
            are active. */}
        <div ref={filterAnchorRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setFilterOpen((v) => !v)}
            className="mono"
            title="Filter visible cards by kind"
            style={{
              fontSize: 10,
              padding: '4px 8px',
              border: `1px solid ${kindFilter.size > 0 ? 'var(--amber)' : 'var(--rule)'}`,
              borderRadius: 3,
              background: kindFilter.size > 0 ? 'var(--amber-soft)' : 'white',
              color: kindFilter.size > 0 ? 'var(--amber)' : 'var(--ink-3)',
              fontWeight: kindFilter.size > 0 ? 700 : 500,
              letterSpacing: '.06em',
              cursor: 'pointer',
            }}
          >
            ▾ Filter{kindFilter.size > 0 && ` · ${kindFilter.size}`}
          </button>
          {filterOpen && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: 0,
                zIndex: 20,
                background: 'white',
                border: '1px solid var(--rule)',
                borderRadius: 5,
                padding: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                minWidth: 280,
                maxWidth: 360,
              }}
            >
              <div
                className="mono"
                style={{
                  fontSize: 9,
                  color: 'var(--ink-3)',
                  letterSpacing: '.1em',
                  fontWeight: 700,
                }}
              >
                FILTER BY KIND
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {KIND_SPECS.map((s) => {
                  const active = kindFilter.has(s.id);
                  return (
                    <button
                      key={s.id}
                      onClick={() => toggleKind(s.id)}
                      className="mono"
                      title={s.description}
                      style={{
                        fontSize: 9.5,
                        padding: '3px 7px',
                        border: `1px solid ${active ? s.rule : 'var(--rule)'}`,
                        borderRadius: 3,
                        background: active ? s.bg : 'transparent',
                        color: active ? s.color : 'var(--ink-3)',
                        fontWeight: active ? 700 : 500,
                        letterSpacing: '.08em',
                        cursor: 'pointer',
                      }}
                    >
                      {s.glyph} {s.label}
                    </button>
                  );
                })}
              </div>
              {kindFilter.size > 0 && (
                <button
                  onClick={() => {
                    clearKinds();
                    setFilterOpen(false);
                  }}
                  className="mono"
                  style={{
                    fontSize: 9.5,
                    padding: '3px 7px',
                    border: '1px solid var(--red)',
                    borderRadius: 3,
                    background: 'transparent',
                    color: 'var(--red)',
                    cursor: 'pointer',
                    alignSelf: 'flex-start',
                  }}
                >
                  × Clear all filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* MAP breadcrumb */}
        {viewMode === 'map' && mapAnchorTitle && (
          <span
            className="mono"
            title={mapAnchorTitle}
            style={{
              fontSize: 9.5,
              color: 'var(--ink-2)',
              letterSpacing: '.04em',
              maxWidth: 240,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              padding: '2px 6px',
              border: '1px solid var(--rule)',
              borderRadius: 3,
              background: 'white',
            }}
          >
            ◐ {mapAnchorTitle}
          </span>
        )}

        {/* Pitch panel count */}
        {viewMode === 'pitch' && anchorTotal > 0 && (
          <>
            <span className="mono" style={{ fontSize: 9.5, color: 'var(--ink-3)', letterSpacing: '.06em' }}>
              PANELS
            </span>
            <select
              value={pitchCount}
              onChange={(e) => setPitchCount(Number(e.target.value))}
              className="mono"
              style={{
                fontSize: 10,
                padding: '3px 5px',
                border: '1px solid var(--rule)',
                borderRadius: 3,
                background: 'white',
                cursor: 'pointer',
              }}
            >
              {[1, 3, 5, anchorTotal].filter((n, i, a) => a.indexOf(n) === i && n > 0).map((n) => (
                <option key={n} value={n}>
                  {n === anchorTotal ? `all (${n})` : `${n}`}
                </option>
              ))}
            </select>
          </>
        )}

        <span style={{ flex: 1 }} />

        {focusId && (
          <>
            <span
              className="mono"
              style={{
                fontSize: 9.5,
                color: 'var(--amber)',
                fontWeight: 700,
                letterSpacing: '.06em',
              }}
            >
              FOCUS {focusId}
            </span>
            <select
              value={focusDepth}
              onChange={(e) => setFocusDepth(Number(e.target.value))}
              className="mono"
              title="N-hop neighborhood depth"
              style={{
                fontSize: 10,
                padding: '2px 4px',
                border: '1px solid var(--rule)',
                borderRadius: 3,
                background: 'white',
                cursor: 'pointer',
              }}
            >
              <option value={1}>1-hop</option>
              <option value={2}>2-hop</option>
              <option value={3}>3-hop</option>
            </select>
            <button
              onClick={clearFocus}
              className="mono"
              title="ESC also exits focus"
              style={{
                fontSize: 9.5,
                padding: '2px 8px',
                border: '1px solid var(--rule)',
                borderRadius: 3,
                background: 'white',
                color: 'var(--ink-2)',
                cursor: 'pointer',
              }}
            >
              EXIT ↩
            </button>
          </>
        )}

        <button
          onClick={() => setLayoutDir(layoutDir === 'LR' ? 'TB' : 'LR')}
          className="mono"
          title={`Layout: ${layoutDir} (click to switch)`}
          style={{
            fontSize: 11,
            padding: '2px 8px',
            border: '1px solid var(--rule)',
            borderRadius: 3,
            background: 'white',
            color: 'var(--ink-2)',
            cursor: 'pointer',
          }}
        >
          {layoutDir === 'LR' ? '→' : '↓'}
        </button>
        <button
          onClick={onExport}
          disabled={exporting}
          className="mono"
          title="Export all cards as .zip — cards, deletions, full history, threads"
          style={{
            fontSize: 10,
            padding: '2px 8px',
            border: '1px solid var(--rule)',
            borderRadius: 3,
            background: 'white',
            color: 'var(--ink-2)',
            cursor: exporting ? 'default' : 'pointer',
            opacity: exporting ? 0.6 : 1,
          }}
        >
          {exporting ? '⤓ …' : '⤓ ZIP'}
        </button>
        <button
          onClick={onExportPng}
          className="mono"
          title="Export visible graph as PNG"
          style={{
            fontSize: 10,
            padding: '2px 8px',
            border: '1px solid var(--rule)',
            borderRadius: 3,
            background: 'white',
            color: 'var(--ink-2)',
            cursor: 'pointer',
          }}
        >
          ⬇ PNG
        </button>
        <button
          onClick={onShare}
          className="mono"
          title="Share this graph — 1080×1440 image card + public read-only link"
          style={{
            fontSize: 10,
            padding: '2px 8px',
            border: '1px solid var(--ink-0)',
            borderRadius: 3,
            background: 'white',
            color: 'var(--ink-0)',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          ↗ Share
        </button>
        <span
          className="mono"
          title={
            visibleCount === totalCount
              ? `${totalCount} cards visible`
              : `${visibleCount} of ${totalCount} cards visible (some hidden by filter)`
          }
          style={{ fontSize: 9.5, color: 'var(--ink-3)', letterSpacing: '.06em' }}
        >
          {visibleCount === totalCount ? totalCount : `${visibleCount}/${totalCount}`}
        </span>
        {staleCount > 0 && (
          <span
            className="mono"
            title={`${staleCount} stale card(s) in current view — a dependency changed, needs review`}
            style={{
              fontSize: 9.5,
              color: 'var(--amber)',
              letterSpacing: '.06em',
              fontWeight: 700,
            }}
          >
            ⚠ {staleCount}
          </span>
        )}
      </div>

    </div>
  );
}
